import { Focus, Ruler, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useStore } from "../../app/store.ts";
import { canSolve, solveCamera } from "../../viewport/cameraSolver.ts";
import { startCameraTransition } from "../../viewport/cameraTransition.ts";
import { Button, IconButton } from "../Button.tsx";
import { SidebarCard } from "../SidebarCard.tsx";
import {
  Divider,
  SectionLabel,
  SegmentedControl,
  SliderRow,
} from "../shared.tsx";
import { showToast } from "../toastBus.ts";
import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG,
  COLOR_AXIS_X,
  COLOR_AXIS_Y,
  COLOR_AXIS_Z,
  COLOR_BORDER_STRONG,
  COLOR_DANGER,
  COLOR_SURFACE,
  COLOR_TEXT_DIM,
  COLOR_TEXT_MUTED,
  COLOR_TEXT_SECONDARY,
  COLOR_WARNING,
  FONT_SIZE_BODY,
  FONT_SIZE_CAPTION,
  FONT_SIZE_SMALL,
  RADIUS_CONTROL,
} from "../tokens.ts";

const AXIS_COLORS: Record<string, string> = {
  x: COLOR_AXIS_X,
  y: COLOR_AXIS_Y,
  z: COLOR_AXIS_Z,
};

/** Resolve natural image dimensions from a blob URL. */
function getImageNaturalSize(url: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = url;
  });
}

/** Run the solver against the current store state and apply the result directly
 *  (no animation) for real-time feedback, or with animation for one-shot solve. */
function runSolve(layerId: string, animate: boolean) {
  const state = useStore.getState();
  const layer = state.referenceLayers.find((l) => l.id === layerId);
  if (!layer) return;
  const vLines = layer.vanishingLines ?? [];
  if (!canSolve(vLines)) return;

  getImageNaturalSize(layer.imageUrl).then(({ w, h }) => {
    const s = useStore.getState();
    const dx = s.cameraPosition[0] - s.cameraTarget[0];
    const dy = s.cameraPosition[1] - s.cameraTarget[1];
    const dz = s.cameraPosition[2] - s.cameraTarget[2];
    const orbitDist = Math.sqrt(dx * dx + dy * dy + dz * dz);

    // Pass the current camera forward direction as a hint to resolve the
    // 180° sign ambiguity (VPs don't distinguish +X from −X).
    const currentFwd: [number, number, number] =
      orbitDist > 1e-6
        ? [-dx / orbitDist, -dy / orbitDist, -dz / orbitDist]
        : [0, 1, 0];

    const result = solveCamera(
      vLines,
      w,
      h,
      layer.groundPlaneZ ?? 0,
      orbitDist,
      currentFwd,
      layer.referenceDistance,
    );
    if (!result) return;
    if ("error" in result) {
      if (animate) showToast(result.error, { severity: "warning" });
      return;
    }

    // Always go through the camera transition system so OrbitControls is
    // updated directly. The store-only path (setCameraOrbit) fights with
    // OrbitControls damping and causes erratic behaviour.
    startCameraTransition(
      result.position,
      result.target,
      result.focalLengthMm,
      null,
      animate ? undefined : 0,
    );
  });
}

export function ReferenceSidebarPanel({ bare }: { bare?: boolean }) {
  const activeReferenceLayerId = useStore((s) => s.activeReferenceLayerId);
  const referenceLayers = useStore((s) => s.referenceLayers);
  const setRefLayerOffset = useStore((s) => s.setRefLayerOffset);
  const setRefLayerScale = useStore((s) => s.setRefLayerScale);
  const setRefLayerRotation = useStore((s) => s.setRefLayerRotation);
  const setRefLayerAbove = useStore((s) => s.setRefLayerAbove);
  const transformMode = useStore((s) => s.transformMode);
  const setTransformMode = useStore((s) => s.setTransformMode);
  const alignCameraDrawAxis = useStore((s) => s.alignCameraDrawAxis);
  const setAlignCameraDrawAxis = useStore((s) => s.setAlignCameraDrawAxis);
  const selectedVanishingLineId = useStore((s) => s.selectedVanishingLineId);
  const pushUndo = useStore((s) => s.pushUndo);
  const deleteVanishingLine = useStore((s) => s.deleteVanishingLine);
  const clearVanishingLines = useStore((s) => s.clearVanishingLines);
  const setGroundPlaneZ = useStore((s) => s.setGroundPlaneZ);
  const setLiveSolve = useStore((s) => s.setLiveSolve);
  const setReferenceDistance = useStore((s) => s.setReferenceDistance);

  const cameraPosition = useStore((s) => s.cameraPosition);
  const cameraTarget = useStore((s) => s.cameraTarget);

  const activeLayer =
    referenceLayers.find((l) => l.id === activeReferenceLayerId) ?? null;

  const Wrapper = bare ? "div" : SidebarCard;

  // Current orbit distance (camera to target)
  const orbitDist = useMemo(() => {
    const dx = cameraPosition[0] - cameraTarget[0];
    const dy = cameraPosition[1] - cameraTarget[1];
    const dz = cameraPosition[2] - cameraTarget[2];
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }, [cameraPosition, cameraTarget]);

  const handleResetTransform = useCallback(() => {
    if (!activeReferenceLayerId) return;
    setRefLayerOffset(activeReferenceLayerId, [0, 0]);
    setRefLayerScale(activeReferenceLayerId, 1);
    setRefLayerRotation(activeReferenceLayerId, 0);
  }, [
    activeReferenceLayerId,
    setRefLayerOffset,
    setRefLayerScale,
    setRefLayerRotation,
  ]);

  const isAlignMode = transformMode === "alignCamera";
  const vanishingLines = activeLayer?.vanishingLines ?? [];
  const groundPlaneZ = activeLayer?.groundPlaneZ ?? 0;
  const liveSolve = activeLayer?.liveSolve ?? false;
  const refDist = activeLayer?.referenceDistance;
  const solvable = canSolve(vanishingLines);

  const lineCounts: Record<string, number> = { x: 0, y: 0 };
  for (const l of vanishingLines) {
    if (l.axis === "x" || l.axis === "y") lineCounts[l.axis]++;
  }

  // --- Live solve: subscribe to vanishing line changes ---
  // We use a store subscription to detect changes to the active layer's
  // vanishingLines or groundPlaneZ and re-run the solver automatically.
  const prevLinesRef = useRef<string>("");

  useEffect(() => {
    if (!liveSolve || !isAlignMode || !activeReferenceLayerId) return;

    const unsub = useStore.subscribe((state) => {
      const layer = state.referenceLayers.find(
        (l) => l.id === activeReferenceLayerId,
      );
      if (!layer) return;
      // Serialize a fingerprint of lines + groundZ to detect changes
      const fingerprint = JSON.stringify({
        lines: layer.vanishingLines,
        gz: layer.groundPlaneZ,
        ref: layer.referenceDistance,
      });
      if (fingerprint !== prevLinesRef.current) {
        prevLinesRef.current = fingerprint;
        runSolve(activeReferenceLayerId, false);
      }
    });

    // Run an initial solve immediately on enabling live mode
    runSolve(activeReferenceLayerId, false);

    return unsub;
  }, [liveSolve, isAlignMode, activeReferenceLayerId]);

  const handleToggleAlignCamera = useCallback(() => {
    if (isAlignMode) {
      setTransformMode("refImage");
    } else {
      setTransformMode("alignCamera");
    }
  }, [isAlignMode, setTransformMode]);

  const handleSolve = useCallback(() => {
    if (!activeReferenceLayerId || !solvable) return;
    runSolve(activeReferenceLayerId, true);
  }, [activeReferenceLayerId, solvable]);

  const handleDeleteSelected = useCallback(() => {
    if (!activeReferenceLayerId || !selectedVanishingLineId) return;
    pushUndo();
    deleteVanishingLine(activeReferenceLayerId, selectedVanishingLineId);
  }, [
    activeReferenceLayerId,
    selectedVanishingLineId,
    pushUndo,
    deleteVanishingLine,
  ]);

  /** Dolly: move camera along its forward axis. Preserves rotation and focal length. */
  const handleDolly = useCallback((newDist: number) => {
    const s = useStore.getState();
    const pos = s.cameraPosition;
    const tgt = s.cameraTarget;
    const dx = pos[0] - tgt[0];
    const dy = pos[1] - tgt[1];
    const dz = pos[2] - tgt[2];
    const curDist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (curDist < 1e-6) return;
    const scale = Math.max(0.1, newDist) / curDist;
    s.setCameraOrbit(
      [tgt[0] + dx * scale, tgt[1] + dy * scale, tgt[2] + dz * scale],
      tgt,
    );
  }, []);

  const handleClearLines = useCallback(() => {
    if (!activeReferenceLayerId || vanishingLines.length === 0) return;
    pushUndo();
    clearVanishingLines(activeReferenceLayerId);
  }, [
    activeReferenceLayerId,
    vanishingLines.length,
    pushUndo,
    clearVanishingLines,
  ]);

  if (!activeLayer) return null;

  return (
    <Wrapper>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 8,
        }}
      >
        <Button
          onClick={handleResetTransform}
          title="Reset position, scale, and rotation"
          variant="secondary"
          size="xs"
        >
          Reset
        </Button>
      </div>

      <SectionLabel>Transform</SectionLabel>
      <SliderRow
        label="Scale"
        value={Math.round(activeLayer.scale * 100)}
        min={10}
        max={500}
        displayValue={`${Math.round(activeLayer.scale * 100)}%`}
        onChange={(v) => setRefLayerScale(activeLayer.id, v / 100)}
        defaultValue={100}
        midpoint={100}
      />
      <SliderRow
        label="Rotation"
        value={Math.round(activeLayer.rotation)}
        min={-180}
        max={180}
        displayValue={`${Math.round(activeLayer.rotation)}°`}
        onChange={(v) => setRefLayerRotation(activeLayer.id, v)}
        defaultValue={0}
      />
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}
      >
        <span
          style={{
            fontSize: FONT_SIZE_BODY,
            color: COLOR_TEXT_DIM,
            width: 90,
            flexShrink: 0,
          }}
        >
          Depth
        </span>
        <SegmentedControl
          options={[
            { label: "Below", value: "below" as const },
            { label: "Above", value: "above" as const },
          ]}
          value={activeLayer.above ? "above" : "below"}
          onChange={(v) => setRefLayerAbove(activeLayer.id, v === "above")}
        />
      </div>

      <Divider />

      <SectionLabel>Camera Alignment</SectionLabel>

      <div style={{ display: "flex", gap: 8, width: "100%" }}>
        <Button
          onClick={handleToggleAlignCamera}
          title="Draw vanishing lines to match camera perspective"
          variant="secondary"
          size="sm"
          fullWidth
          leftIcon={<Focus size={14} />}
          style={{
            flex: 1,
            ...(isAlignMode
              ? {
                  background: COLOR_ACTIVE_BG,
                  borderColor: COLOR_ACCENT,
                  color: COLOR_ACCENT,
                }
              : {}),
          }}
        >
          {isAlignMode ? "Exit" : "Align"}
        </Button>
      </div>

      {isAlignMode && (
        <div
          style={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {/* Axis selector */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: FONT_SIZE_BODY,
                color: COLOR_TEXT_DIM,
                width: 90,
                flexShrink: 0,
              }}
            >
              Draw Axis
            </span>
            <div style={{ display: "flex", gap: 2, flex: 1 }}>
              {(["x", "y"] as const).map((axis) => (
                <Button
                  key={axis}
                  onClick={() => setAlignCameraDrawAxis(axis)}
                  variant="secondary"
                  size="xs"
                  style={{
                    flex: 1,
                    padding: "4px 0",
                    fontSize: FONT_SIZE_SMALL,
                    fontWeight: 600,
                    borderRadius: RADIUS_CONTROL,
                    border: `0.5px solid ${alignCameraDrawAxis === axis ? AXIS_COLORS[axis] : COLOR_BORDER_STRONG}`,
                    background:
                      alignCameraDrawAxis === axis
                        ? `${AXIS_COLORS[axis]}22`
                        : COLOR_SURFACE,
                    color:
                      alignCameraDrawAxis === axis
                        ? AXIS_COLORS[axis]
                        : COLOR_TEXT_MUTED,
                    cursor: "pointer",
                    textTransform: "uppercase",
                  }}
                >
                  {axis}
                </Button>
              ))}
            </div>
          </div>

          {/* Ground plane Z slider */}
          <SliderRow
            label="Ground Z"
            value={groundPlaneZ}
            min={-10}
            max={10}
            step={0}
            displayValue={`${groundPlaneZ.toFixed(2)}`}
            onChange={(v) => setGroundPlaneZ(activeLayer.id, v)}
          />

          {/* Reference distance — ground-plane scale anchor */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: FONT_SIZE_BODY,
                color: COLOR_TEXT_DIM,
                width: 90,
                flexShrink: 0,
              }}
            >
              Ref Dist
            </span>
            <Button
              onClick={() =>
                setAlignCameraDrawAxis(
                  alignCameraDrawAxis === "ref" ? null : "ref",
                )
              }
              title="Draw a reference distance on the ground plane"
              variant="secondary"
              size="xs"
              leftIcon={<Ruler size={12} />}
              style={{
                flex: 1,
                ...(alignCameraDrawAxis === "ref"
                  ? {
                      background: `${COLOR_WARNING}22`,
                      borderColor: COLOR_WARNING,
                      color: COLOR_WARNING,
                    }
                  : {}),
              }}
            >
              {refDist ? "Redraw" : "Place"}
            </Button>
            {refDist && (
              <IconButton
                onClick={() => {
                  pushUndo();
                  setReferenceDistance(activeLayer.id, undefined);
                }}
                title="Clear reference distance"
                label="Clear reference distance"
                icon={<X size={12} />}
                variant="secondary"
                size="xs"
                style={{
                  color: COLOR_DANGER,
                }}
              />
            )}
          </div>
          {refDist && (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span
                style={{
                  fontSize: FONT_SIZE_BODY,
                  color: COLOR_TEXT_DIM,
                  width: 90,
                  flexShrink: 0,
                }}
              >
                Length
              </span>
              <input
                type="number"
                min={0.01}
                step={0.1}
                value={refDist.length}
                onChange={(e) => {
                  const val = Number.parseFloat(e.target.value);
                  if (Number.isFinite(val) && val > 0) {
                    setReferenceDistance(activeLayer.id, {
                      ...refDist,
                      length: val,
                    });
                  }
                }}
                style={{
                  flex: 1,
                  padding: "4px 6px",
                  fontSize: FONT_SIZE_SMALL,
                  borderRadius: RADIUS_CONTROL,
                  border: `0.5px solid ${COLOR_BORDER_STRONG}`,
                  background: COLOR_SURFACE,
                  color: COLOR_TEXT_SECONDARY,
                  width: 0,
                }}
              />
            </div>
          )}

          {/* Dolly (camera distance) — log-scale, right = closer */}
          {!refDist && (
            <SliderRow
              label="Dolly"
              value={-Math.log(orbitDist)}
              min={-Math.log(50)}
              max={-Math.log(0.1)}
              step={0}
              displayValue={`${orbitDist.toFixed(2)}`}
              onChange={(v) => handleDolly(Math.exp(-v))}
            />
          )}

          {/* Line counts */}
          <div
            style={{
              fontSize: FONT_SIZE_CAPTION,
              color: COLOR_TEXT_DIM,
              display: "flex",
              gap: 12,
            }}
          >
            {(["x", "y"] as const).map((axis) => (
              <span
                key={axis}
                style={{
                  color: lineCounts[axis] >= 2 ? AXIS_COLORS[axis] : undefined,
                }}
              >
                {axis.toUpperCase()}: {lineCounts[axis]}
              </span>
            ))}
          </div>

          {/* Line list */}
          {vanishingLines.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxHeight: 120,
                overflow: "auto",
              }}
            >
              {vanishingLines.map((line) => (
                <div
                  key={line.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "3px 6px",
                    borderRadius: 4,
                    fontSize: FONT_SIZE_CAPTION,
                    background:
                      selectedVanishingLineId === line.id
                        ? COLOR_ACTIVE_BG
                        : "transparent",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    useStore.getState().setSelectedVanishingLine(line.id)
                  }
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      background: AXIS_COLORS[line.axis],
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: COLOR_TEXT_SECONDARY, flex: 1 }}>
                    {line.axis.toUpperCase()} Line
                  </span>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      pushUndo();
                      deleteVanishingLine(activeLayer.id, line.id);
                    }}
                    icon={<X size={12} />}
                    label="Delete line"
                    variant="ghost"
                    size="xs"
                    style={{
                      color: COLOR_TEXT_DIM,
                      padding: 2,
                    }}
                    title="Delete line"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Live Solve toggle + Solve button row */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: FONT_SIZE_BODY,
                color: COLOR_TEXT_DIM,
                width: 90,
                flexShrink: 0,
              }}
            >
              Live Solve
            </span>
            <SegmentedControl
              options={[
                { label: "Off", value: "off" as const },
                { label: "On", value: "on" as const },
              ]}
              value={liveSolve ? "on" : "off"}
              onChange={(v) => setLiveSolve(activeLayer.id, v === "on")}
            />
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 6 }}>
            <Button
              onClick={handleSolve}
              disabled={!solvable}
              title={
                solvable
                  ? "Solve camera from vanishing lines"
                  : "Need at least 2 lines on each of 2 axes"
              }
              variant={solvable ? "primary" : "secondary"}
              size="xs"
              style={{
                flex: 1,
              }}
            >
              Solve
            </Button>
            {selectedVanishingLineId && (
              <IconButton
                onClick={handleDeleteSelected}
                title="Delete selected line"
                label="Delete selected line"
                icon={<Trash2 size={12} />}
                variant="secondary"
                size="xs"
                style={{
                  color: COLOR_DANGER,
                }}
              />
            )}
            {vanishingLines.length > 0 && (
              <Button
                onClick={handleClearLines}
                title="Clear all lines"
                variant="secondary"
                size="xs"
                style={{
                  color: COLOR_DANGER,
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      )}
    </Wrapper>
  );
}
